import { Button, Grid, GridItem, Input } from '@chakra-ui/react';
import { isFalse, isNotEmpty, isTrue, populate } from 'my-easy-fp';
import React from 'react';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { AiOutlineSplitCells } from 'react-icons/ai';
import { useRecoilState, useRecoilValue } from 'recoil';
import { textLayoutAtom, windowSizeAtom } from '../../recoil/atom/counter';
import { onlyKoreanCharCountSelector } from '../../recoil/selector/counter';
import { getMarginSize, getRectSize } from '../../tools/calculator';

const ControlBox: React.FC = () => {
  const [windowSizeState] = useRecoilState(windowSizeAtom);
  const [textLayoutState, setTextLayoutState] = useRecoilState(textLayoutAtom);
  const koreanChars = useRecoilValue(onlyKoreanCharCountSelector);

  const onHandleLeftShiftLetter = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const index = parseInt(event.currentTarget.dataset.index ?? 'NaN', 10);

    if (isTrue(isNaN(index))) {
      return false;
    }

    const letters = [...textLayoutState.letters.map((layout) => [...layout])];
    const letter = letters[index].pop();

    if (index - 1 >= 0 && isNotEmpty(letter)) {
      letters[index - 1] = [letter, ...letters[index - 1]];
    }

    const size = getMarginSize({ letters, size: { width: windowSizeState.width, height: windowSizeState.height } });
    const { widths, heights } = getRectSize({
      size: { width: size.width, height: windowSizeState.height },
      letters,
      margin: size.margin,
    });

    setTextLayoutState({ ...textLayoutState, letters: letters, character: { widths, heights, margin: size.margin } });
  };

  const onHandleRightShiftLetter = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const index = parseInt(event.currentTarget.dataset.index ?? 'NaN', 10);

    if (isTrue(isNaN(index))) {
      return false;
    }

    const letters = [...textLayoutState.letters.map((layout) => [...layout])];
    const letter = letters[index].pop();

    if (index + 1 < letters.length && isNotEmpty(letter)) {
      letters[index + 1] = [letter, ...letters[index + 1]];
    }

    const size = getMarginSize({ letters, size: { width: windowSizeState.width, height: windowSizeState.height } });
    const { widths, heights } = getRectSize({
      size: { width: size.width, height: windowSizeState.height },
      letters,
      margin: size.margin,
    });

    setTextLayoutState({ ...textLayoutState, letters: letters, character: { widths, heights, margin: size.margin } });
  };

  const onHandleSplitLetter = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const index = parseInt(event.currentTarget.dataset.index ?? 'NaN', 10);

    if (isTrue(isNaN(index))) {
      return false;
    }

    const letters = [...textLayoutState.letters.map((layout) => [...layout])];
    const head = letters[index].slice(0, Math.floor(letters.length / 2));
    const tail = letters[index].slice(Math.floor(letters.length / 2), letters.length);

    const headLetters = letters.slice(0, index);
    const tailLetters = letters.slice(index + 1, letters.length);
    const newLetters = [...headLetters, head, tail, ...tailLetters];

    const size = getMarginSize({ letters, size: { width: windowSizeState.width, height: windowSizeState.height } });
    const { widths, heights } = getRectSize({
      size: { width: size.width, height: windowSizeState.height },
      letters: newLetters,
      margin: size.margin,
    });

    setTextLayoutState({ ...textLayoutState, letters: newLetters, character: { widths, heights, margin: size.margin } });
  };

  return (
    <Grid
      w={`${windowSizeState.width}px`}
      marginBottom="30"
      paddingLeft={textLayoutState.character.margin.left}
      paddingRight={textLayoutState.character.margin.right}
      style={{ visibility: koreanChars > 20 ? 'visible' : 'hidden' }}
      templateColumns={`repeat(${textLayoutState.maxRowSize}, 1fr)`}
      templateRows={`repeat(3, 1fr)`}
      gap={0}
    >
      {populate(textLayoutState.maxRowSize).map((index) => {
        return (
          <GridItem key={index}>
            <Input
              data-index={index}
              type="number"
              paddingLeft="5px"
              readOnly={true}
              paddingRight="5px"
              size="md"
              value={textLayoutState.letters[index].length}
              onChange={() => {}}
            />
          </GridItem>
        );
      })}

      {/* 좌측으로 글자를 보낸다  */}
      {populate(textLayoutState.maxRowSize).map((index) => {
        return (
          <GridItem key={index}>
            <Button data-index={index} disabled={index === 0} onClick={onHandleLeftShiftLetter}>
              <FaAngleLeft />
            </Button>
          </GridItem>
        );
      })}

      {/* 우측으로 글자를 보낸다 */}
      {populate(textLayoutState.maxRowSize).map((index) => {
        return (
          <GridItem key={index}>
            <Button
              data-index={index}
              disabled={index === textLayoutState.maxRowSize - 1}
              onClick={onHandleRightShiftLetter}
            >
              <FaAngleRight />
            </Button>
          </GridItem>
        );
      })}

      {/* 줄을 분할한다 */}
      {populate(textLayoutState.maxRowSize).map((index) => {
        return (
          <GridItem key={index}>
            <Button
              data-index={index}
              disabled={isFalse(index !== 0 && textLayoutState.letters[index].length > 2)}
              onClick={onHandleSplitLetter}
            >
              <AiOutlineSplitCells />
            </Button>
          </GridItem>
        );
      })}
    </Grid>
  );
};

export default ControlBox;
